import React from 'react';
import {
  ADDRESS_TEXT,
  GOOGLE_BUSINESS_URL,
  MAP_LABEL,
  MAP_LINK_URL,
  PHONE_DISPLAY,
  PHONE_TEL,
} from '../lib/site';

interface FooterProps {
  brandName?: string;
}

export const Footer: React.FC<FooterProps> = ({ brandName = "Barber" }) => {
  return (
    <footer id="reservation" className="bg-white relative z-50 pt-24 pb-36 md:pb-48 lg:pb-[30rem] overflow-hidden">
      
      {/* Container for the Card */}
      <div className="relative w-full px-4 z-10">
         <div className="max-w-[95rem] mx-auto bg-white rounded-[2.5rem] px-8 py-12 md:px-16 md:py-20 shadow-xl border border-gray-100">
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
               
               {/* Brand Column */}
               <div className="lg:col-span-5 flex flex-col justify-between h-full">
                  <div className="space-y-6">
                     <div className="flex flex-col leading-none">
                        <span className="text-[10px] uppercase tracking-[0.26em] text-gray-500 mb-1">{brandName}</span>
                        <span className="text-2xl font-bold tracking-tight">Eleganze</span>
                     </div>
                     <p className="text-gray-500 max-w-sm text-lg leading-relaxed">
                        Votre barber à Poitiers, installé à Buxerolles.
                        Coupe homme, barbe et soins premium dans un cadre soigné.
                     </p>

                     <div className="flex flex-col gap-4 text-sm">
                        <div className="flex flex-col">
                           <span className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-600">
                             Adresse du salon
                           </span>
                           <div className="mt-1 flex flex-col gap-1.5">
                             <a
                               href={MAP_LINK_URL}
                               target="_blank"
                               rel="noopener noreferrer"
                               className="inline-flex min-h-11 items-center text-gray-700 transition-colors font-medium hover:text-[#1d1d1d]"
                             >
                               {ADDRESS_TEXT}
                             </a>
                             <a
                               href={MAP_LINK_URL}
                               target="_blank"
                               rel="noopener noreferrer"
                               className="inline-flex min-h-11 items-center text-xs font-semibold text-gray-600 transition-colors underline underline-offset-2 hover:text-[#1d1d1d]"
                             >
                               {MAP_LABEL}
                             </a>
                           </div>
                        </div>
                        
                        <div className="flex flex-col">
                           <span className="mb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-600">
                             Téléphone du salon
                           </span>
                           <a
                             href={`tel:${PHONE_TEL}`}
                             className="inline-flex min-h-11 items-center text-gray-700 transition-colors font-semibold hover:text-[#1d1d1d]"
                           >
                             {PHONE_DISPLAY}
                           </a>
                        </div>
                     </div>
                     
                     {/* Social Icons */}
                     <div className="flex items-center gap-4 mt-4">
                        <a href="https://www.instagram.com/barber_eleganze?igsh=MXk4NXh2eDZzc3Zy" target="_blank" rel="noopener noreferrer" aria-label="Instagram Eleganze" className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#1d1d1d] hover:bg-[#1d1d1d] hover:text-white transition-colors">
                           <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm8.5 1.8h-8.5A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95Zm-4.25 2.95a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.8a3.45 3.45 0 1 0 0 6.9 3.45 3.45 0 0 0 0-6.9Zm5.55-2.11a1.21 1.21 0 1 1 0 2.42 1.21 1.21 0 0 1 0-2.42Z" /></svg>
                        </a>
                        <a href="https://www.tiktok.com/@eleganze_barber?_r=1&_t=ZN-94BXCZvfJp8" target="_blank" rel="noopener noreferrer" aria-label="TikTok Eleganze" className="w-10 h-10 rounded-full bg-white border border-gray-200 flex items-center justify-center text-[#1d1d1d] hover:bg-[#1d1d1d] hover:text-white transition-colors">
                           <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 2.78-1.15 5.54-3.33 7.31-1.92 1.57-4.58 2.07-6.99 1.53-2.34-.52-4.41-2.01-5.53-4.13-1.15-2.17-1.25-4.84-.25-7.09 1.01-2.28 3.01-4.04 5.43-4.66 1.41-.36 2.91-.4 4.35-.15v4.02c-1.03-.19-2.12-.08-3.05.42-.95.5-1.68 1.35-2.02 2.34-.35 1.02-.31 2.18.12 3.16.42.97 1.25 1.74 2.23 2.13.98.39 2.1.4 3.09.06.97-.33 1.78-1.03 2.26-1.92.46-.86.64-1.85.64-2.84V.02z" clipRule="evenodd" /></svg>
                        </a>
                     </div>
                  </div>
               </div>

               {/* Links Columns */}
               <div className="lg:col-span-7 flex flex-wrap gap-12 lg:gap-24 lg:justify-end">
                  
                  <div className="flex flex-col gap-4">
                     <h3 className="font-bold text-lg mb-2">Navigation</h3>
                     <a href="/#accueil" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Accueil</a>
                     <a href="/#soins-coiffure" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Soins coiffure</a>
                     <a href="/#onglerie" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Onglerie</a>
                     <a href="/#horaires" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Horaires</a>
                     <a href="/#contact" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Contact</a>
                     <a href="/#avis" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Avis</a>
                  </div>

                  <div className="flex flex-col gap-4">
                     <h3 className="font-bold text-lg mb-2">Horaires</h3>
                     <span className="text-gray-500 font-medium">Lundi : Fermé</span>
                     <span className="text-gray-500 font-medium">Mar - Ven : 09h00 - 19h00</span>
                     <span className="text-gray-500 font-medium">Samedi : 09h00 - 18h00</span>
                     <span className="text-gray-500 font-medium">Dimanche : Fermé</span>
                  </div>

                  <div className="flex flex-col gap-4">
                     <h3 className="font-bold text-lg mb-2">Réseaux</h3>
                     <a href="https://www.instagram.com/barber_eleganze?igsh=MXk4NXh2eDZzc3Zy" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Instagram</a>
                     <a href="https://www.tiktok.com/@eleganze_barber?_r=1&_t=ZN-94BXCZvfJp8" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">TikTok</a>
                     <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-10 items-center text-gray-500 hover:text-[#1d1d1d] transition-colors font-medium">Google Business</a>
                  </div>

               </div>
            </div>

            {/* Bottom Bar inside Card */}
            <div className="mt-16 pt-8 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-gray-500 relative z-20">
               <span>© 2026 Eleganze Barber. Tous droits réservés.</span>
               <div className="flex gap-8 items-center">
                  <a href="/mentions-legales" className="hover:text-[#1d1d1d] transition-colors font-medium">
                    Mentions légales
                  </a>
                  <a href="https://agence-balrog.fr/" target="_blank" rel="noopener noreferrer" className="hover:text-[#1d1d1d] transition-colors font-medium">
                    Créé par l&apos;agence web Balrog
                  </a>
               </div>
            </div>
         </div>
      </div>

      {/* Giant Watermark Text on White Background - Bottom aligned with more space */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center items-end pointer-events-none select-none z-0 pb-6 md:pb-10 lg:pb-24">
         <div className="text-[20vw] md:text-[18vw] lg:text-[23vw] leading-[0.8] font-black tracking-tighter text-gray-100 transform translate-y-0">
           Eleganze
         </div>
      </div>
    </footer>
  );
};
